"use client";

import React, { useState } from "react";
import { CircleQuestionMark as HelpCircle, Phone, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const faqItems = [{
  id: "item-1",
  question: "Are the treatments safe for children, dogs, and cats?",
  answer: "Yes. We use targeted, biodegradable formulations that dry clear and remain strictly locked to applied surfaces. For interior treatments, we ask that children and household pets remain off treated floors until fully dry, typically two to three hours. Exterior perimeter barrier sprays settle rapidly without leaving airborne residues, protecting local birdlife, garden beds, and domestic pets once dried."
}, {
  id: "item-2",
  question: "How long does a pest treatment remain effective?",
  answer: "Standard residential barrier applications provide active defense for six to twelve months depending on the pest species, indoor moisture, and direct weather exposure. Exterior boundary washes against crawling insects and spiders typically hold strong for six months in Auckland's humid climate, while indoor residual applications and bait stations protect your property through seasonal breeding cycles."
}, {
  id: "item-3",
  question: "What signs indicate an active pest infestation?",
  answer: "Early warning signs include small dark droppings along baseboards or under kitchen sinks, scratching sounds in ceiling cavities after sunset, fine sawdust shavings near timber joints, chewed electrical cable insulation, and recurring daylight sightings of insects near plumbing penetrations. Noticing any of these warrants a thorough site inspection before nests expand."
}, {
  id: "item-4",
  question: "Do I need to vacate the property during treatment?",
  answer: "Exterior treatments require no vacancy; your family can remain comfortably inside with windows and external doors closed. For comprehensive interior misting or flea treatments, we recommend vacating the home for two to three hours to allow the products to settle and dry. When you return, open windows for twenty minutes to restore fresh airflow."
}, {
  id: "item-5",
  question: "Do you offer warranty follow-up visits if pests return?",
  answer: "Every full-home treatment includes our Auckland service warranty. If targeted pests reappear within your specific warranty window (ranging from three to six months based on the targeted species), our technicians return and apply corrective treatments at zero additional charge. We stay committed until your property remains completely clear."
}];
export default function Faqs() {
  const shouldReduceMotion = useReducedMotion();
  const [openItem, setOpenItem] = useState<string>("item-1");
  return <section id="faqs" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground overflow-hidden">
      <div className="container mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-secondary-foreground/20 bg-secondary px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground mb-4">
            <ShieldCheck className="size-3.5 text-primary" />
            <span>Pest Control Advice</span>
          </div>

          <div className="mx-auto mb-3 h-0.5 w-9 bg-primary" />

          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Frequently asked pest control questions
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Clear details on Auckland safety guidelines, treatment longevity, and our re-treatment guarantee.
          </p>
        </div>

        {/* Accordion and Help Box Layout */}
        <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Main FAQ Accordion */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: "easeOut"
        }} className="lg:col-span-8 rounded-xl border border-border bg-card p-6 md:p-8 shadow-xs">
            <Accordion type="single" collapsible value={openItem} onValueChange={val => setOpenItem(val)} className="w-full space-y-4">
              {faqItems.map((faq, index) => <div key={faq.id} data-index={index}>
                  <AccordionItem value={faq.id} className="border-b border-border py-1 last:border-b-0">
                    <AccordionTrigger className="text-left font-default text-base md:text-lg font-semibold text-foreground hover:text-primary transition-colors py-3">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm md:text-base leading-relaxed pt-1 pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </motion.div>

          {/* Sidebar Assistance Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-4 space-y-6">
            <div className="rounded-xl border border-border bg-muted p-6 md:p-7 shadow-xs">
              <div className="flex size-11 items-center justify-center rounded-lg bg-secondary text-primary mb-4">
                <HelpCircle className="size-6" />
              </div>
              <h3 className="font-default text-lg font-semibold text-foreground">
                Need specific property advice?
              </h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Speak directly with an Auckland technician about wasp nests, rodents, or insect perimeter barriers.
              </p>

              <div className="mt-6 pt-5 border-t border-border space-y-3">
                <div className="flex items-start gap-2.5 text-xs text-muted-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>No obligation, transparent pricing</span>
                </div>
                <div className="flex items-start gap-2.5 text-xs text-muted-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Biodegradable products for homes with pets</span>
                </div>
                <div className="flex items-start gap-2.5 text-xs text-muted-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Serving North Shore to South Auckland</span>
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-3">
                <Button type="button" variant="default" onClick={() => {
                if (typeof window !== "undefined") {
                  window.dispatchEvent(new CustomEvent("eco-open-quote-modal"));
                }
              }} className="w-full justify-center bg-primary text-primary-foreground font-semibold hover:bg-primary/90 cursor-pointer">
                  Request a free quote
                  <ArrowRight className="size-4 ml-1" />
                </Button>

                <Button asChild variant="secondary" className="w-full justify-center border border-primary/20 bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/80">
                  <a href="tel:0221002103">
                    <Phone className="size-4 mr-1.5 text-primary" />
                    Call 022 100 2103
                  </a>
                </Button>
              </div>

              <p className="mt-3 text-center text-xs text-muted-foreground">
                Hours: Monday to Sunday, 7am - 9pm
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}